import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSupabase } from "../SupabaseContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import { Label } from "./ui/label";
import { AtSign, Key, AlertCircle, Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(""); // Premenujem idKod na password
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();
  const supabase = useSupabase();

  // In LoginPage.js, update the useEffect or login success handler:

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      if (user.is_admin) {
        navigate("/companies"); // Redirect admins to companies
      } else if (user.is_manager) {
        navigate("/admin"); // Redirect managers to admin dashboard
      } else {
        navigate("/situations"); // Redirect regular users to situations
      }
    }
  }, [navigate]);

  // In LoginPage.js, update the handleLogin function

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // 1. Prihlásenie cez Supabase Auth
      const { data: authData, error: authError } =
        await supabase.auth.signInWithPassword({
          email,
          password,
        });
      if (authError || !authData?.user) {
        throw new Error("Nesprávny email alebo heslo.");
      }

      // 2. Načítaj user z tabuľky users podľa auth_user_id
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select(
          `*,
          companies (
            id,
            name,
            is_active
          )`
        )
        .eq("auth_user_id", authData.user.id)
        .eq("is_active", true)
        .single();

      if (userError || !userData) {
        throw new Error("Váš účet nie je aktívny alebo neexistuje.");
      }

      // Check if user's company is active (skip check for admins)
      if (!userData.is_admin && !userData.companies?.is_active) {
        throw new Error(
          "Váš firemný účet je momentálne neaktívny. Kontaktujte administrátora."
        );
      }

      localStorage.setItem("user", JSON.stringify(userData));

      // Update the navigation logic here
      if (userData.is_admin) {
        navigate("/companies", { replace: true });
      } else if (userData.is_manager) {
        navigate("/admin", { replace: true });
      } else {
        navigate("/situations", { replace: true });
      }
    } catch (error) {
      console.error("Login error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetError("");
    setResetMessage("");
    setResetLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: window.location.origin + "/reset-password",
      });
      if (error) throw error;
      setResetMessage("Na váš email bol odoslaný odkaz na obnovenie hesla.");
    } catch (err) {
      setResetError(
        err.message || "Nepodarilo sa odoslať email na obnovenie hesla."
      );
    } finally {
      setResetLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="mx-6 w-full max-w-md">
      <Card className="shadow-lg">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            Prihlásenie do Soluteria
          </CardTitle>
          <CardDescription className="text-center">
            Zadajte svoje prihlasovacie údaje pre prístup do aplikácie
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 text-gray-400 transform -translate-y-1/2" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vasa@emailova-adresa.sk"
                  className="pl-11"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Heslo</Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 text-gray-400 transform -translate-y-1/2" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Zadajte svoje heslo"
                  className="pr-11 pl-11"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4 text-gray-400" />
                  ) : (
                    <Eye className="w-4 h-4 text-gray-400" />
                  )}
                </Button>
              </div>
            </div>
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Prihlasovanie..." : "Prihlásiť sa"}
            </Button>
          </form>
          <Button
            type="button"
            variant="link"
            className="p-0 mt-1 mb-2 w-full text-sm text-right"
            onClick={() => setShowResetDialog(true)}
          >
            Zabudnuté heslo?
          </Button>
        </CardContent>
      </Card>
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Obnovenie hesla</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="resetEmail">Email</Label>
              <Input
                id="resetEmail"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Zadajte svoj email"
                required
              />
            </div>
            {resetError && (
              <Alert variant="destructive">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>{resetError}</AlertDescription>
              </Alert>
            )}
            {resetMessage && (
              <Alert variant="default">
                <AlertDescription>{resetMessage}</AlertDescription>
              </Alert>
            )}
            <Button type="submit" className="w-full" disabled={resetLoading}>
              {resetLoading ? "Odosielam..." : "Odoslať odkaz na obnovenie"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
